/**
 * Notificações NATIVAS do dispositivo (Notification API via service worker).
 *
 * Contexto: as notificações do app eram 100% internas (sino + toast React),
 * visíveis só com a aba aberta em primeiro plano — no celular "não aparecia
 * nada". Esta camada mostra a notificação do SISTEMA quando o app está em
 * segundo plano (aba em background / outro app em foco) e a permissão foi
 * concedida.
 *
 * Limites de plataforma (não são bugs):
 *  - Android/desktop: funciona com a aba/PWA aberto em background. Com o app
 *    FECHADO ou tela bloqueada, só Web Push real (VAPID) entrega — fora do
 *    escopo desta camada.
 *  - iOS: a Notification API clássica NÃO existe; iPhone só mostra notificação
 *    via Push API + service worker com o app instalado na tela de início
 *    (iOS 16.4+). Esta camada não produz nada no iOS.
 *  - `new Notification()` em página lança "Illegal constructor" no Chrome
 *    Android — por isso o caminho principal é reg.showNotification (SW).
 */

const DISMISSED_KEY = 'infinitsales-native-notif-dismissed';

export function nativeNotificationsSupported() {
  return typeof window !== 'undefined' && 'Notification' in window;
}

export function nativeNotificationsPermission() {
  if (!nativeNotificationsSupported()) return 'unsupported';
  return Notification.permission; // 'default' | 'granted' | 'denied'
}

/** O convite discreto deve aparecer? (suportado, ainda não decidido, não dispensado) */
export function shouldOfferNativeNotifications() {
  if (!nativeNotificationsSupported()) return false;
  if (Notification.permission !== 'default') return false;
  try {
    return localStorage.getItem(DISMISSED_KEY) !== 'true';
  } catch {
    return true;
  }
}

export function dismissNativeNotificationsOffer() {
  try { localStorage.setItem(DISMISSED_KEY, 'true'); } catch {}
}

/** Pedir permissão — chamar SOMENTE em gesto do usuário (clique). */
export async function requestNativeNotifications() {
  if (!nativeNotificationsSupported()) return 'unsupported';
  try {
    const result = await Notification.requestPermission();
    return result;
  } catch {
    return 'denied';
  }
}

/**
 * Mostra a notificação nativa para um doc de notificação do backend, SÓ quando
 * o app está em segundo plano (em foreground o toast interno já cobre — evita
 * duplicidade).
 */
export function maybeShowNativeNotification(doc) {
  try {
    if (!doc) return;
    if (typeof document !== 'undefined' && document.visibilityState === 'visible') return;
    if (!nativeNotificationsSupported()) return;
    if (Notification.permission !== 'granted') return;

    const title = doc.title || 'InfinitSales';
    const options = {
      body: doc.body || '',
      icon: '/logo192.png',
      badge: '/logo192.png',
      tag: doc.id || undefined, // colapsa duplicatas do mesmo doc
      data: { cta: doc.cta || null, notification_id: doc.id || null },
    };

    if (navigator.serviceWorker) {
      // getRegistration (NÃO .ready): sem SW registrado (dev, sessão antiga)
      // o .ready pendura para SEMPRE e o fallback nunca rodava. E exige
      // reg.active — registration em installing rejeita o showNotification.
      navigator.serviceWorker.getRegistration()
        .then((reg) => {
          if (reg && reg.active) return reg.showNotification(title, options);
          // Fallback desktop (Android lança Illegal constructor — engolido).
          try { new Notification(title, options); } catch {}
          return undefined;
        })
        .catch(() => {
          try { new Notification(title, options); } catch {}
        });
    } else {
      try { new Notification(title, options); } catch {}
    }
  } catch {
    // best-effort — notificação nativa nunca pode quebrar o fluxo interno
  }
}
